import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, Skeleton, useToast } from '../../../design-system';
import { money, fmtDate, INV_STATUS } from '../../pages/OrgAdminFinance';
import '../../styles/Finance.css';

// One invoice: what's on it, what's been received, and the actions that move it
// along — issue, receipt, cancel.

const MODES = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'other', label: 'Other' },
];

function InvoiceDrawer({ invoiceId, onClose, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: '', tdsAmount: '', mode: 'bank_transfer', reference: '', receivedOn: new Date().toISOString().slice(0, 10) });
  const [sendInfo, setSendInfo] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  // Pre-selects the firm's usual practice; the sender still chooses each time.
  const [sendKind, setSendKind] = useState('proforma');

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get(`/api/finance/invoices/${invoiceId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load the invoice'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);
  useEffect(load, [load]);

  // Who this would go to, and where it has already gone. Loaded alongside the
  // invoice so the send buttons can say the address before they are pressed —
  // "sent" with no indication of to whom is not a confirmation.
  useEffect(() => {
    apiClient.get(`/api/finance/invoices/${invoiceId}/send-history`)
      .then((r) => setSendInfo(r.data))
      .catch(() => setSendInfo(null));
    apiClient.get('/api/finance/settings')
      .then((r) => setSendKind(r.data.settings?.defaultSendKind || 'proforma'))
      .catch(() => {});
  }, [invoiceId]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      // Some callers toast their own richer message (which address it reached).
      if (successMsg) toast.success(successMsg);
      load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  // Report where it went, not just that it went. A silent "sent" against a
  // client with no email on file is the failure this wording exists to expose.
  const describeDelivery = (d) => {
    if (!d) return '';
    if (d.status === 'sent') return ` — emailed to ${d.to.join(', ')}`;
    if (d.status === 'skipped') {
      return d.to?.length ? ' — not emailed (SMTP not configured)' : ' — NOT emailed: no billing address on file';
    }
    return ' — email failed, see the send history';
  };

  const refreshSend = () => apiClient.get(`/api/finance/invoices/${invoiceId}/send-history`)
    .then((r) => setSendInfo(r.data)).catch(() => {});

  const issue = () => act(async () => {
    const r = await apiClient.post(`/api/finance/invoices/${invoiceId}/issue`);
    await refreshSend();
    return r;
  }, 'Invoice issued');

  const send = (kind) => act(async () => {
    const r = await apiClient.post(`/api/finance/invoices/${invoiceId}/send`, { kind });
    await refreshSend();
    setSendOpen(false);
    toast.success(
      `${kind === 'proforma' ? 'Proforma' : 'Tax invoice'} ${r.data.number} sent${describeDelivery(r.data.delivery)}`
    );
  });

  const resend = () => act(async () => {
    const r = await apiClient.post(`/api/finance/invoices/${invoiceId}/resend`);
    await refreshSend();
    toast.success(`Re-sent${describeDelivery(r.data.delivery)}`);
  });

  // The document needs the auth header, so it is fetched and handed to the
  // browser as a blob rather than opened by URL.
  const viewDocument = async (kind) => {
    try {
      const r = await apiClient.get(`/api/finance/invoices/${invoiceId}/document`, {
        params: kind ? { kind } : {}, responseType: 'text',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not open the document');
    }
  };

  const viewReceipt = async (paymentId) => {
    try {
      const r = await apiClient.get(`/api/finance/payments/${paymentId}/document`, { responseType: 'text' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.danger('Could not open that receipt');
    }
  };

  const cancel = () => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Why is this invoice being cancelled?');
    if (!reason?.trim()) return;
    act(() => apiClient.post(`/api/finance/invoices/${invoiceId}/cancel`, { reason }), 'Invoice cancelled');
  };

  const savePayment = () => act(async () => {
    await apiClient.post(`/api/finance/invoices/${invoiceId}/payments`, {
      amount: Number(pay.amount) || 0,
      tdsAmount: Number(pay.tdsAmount) || 0,
      mode: pay.mode, reference: pay.reference, receivedOn: pay.receivedOn,
    });
    setPayOpen(false);
    setPay((p) => ({ ...p, amount: '', tdsAmount: '', reference: '' }));
  }, 'Receipt recorded');

  const inv = data?.invoice;
  const s = inv ? INV_STATUS[inv.status] : null;

  return (
    <>
    <Modal
      isOpen onClose={onClose}
      title={inv
        ? `${inv.invoiceNumber || inv.proformaNumber || 'Draft invoice'} — ${inv.clientName}`
        : 'Invoice'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {inv && (inv.invoiceNumber || inv.proformaNumber || inv.status === 'draft') && (
            <Button variant="ghost" onClick={() => viewDocument()} disabled={busy}>View document</Button>
          )}
          {/* One Send action; the dialog is where proforma-or-tax-invoice is
              chosen, because that choice belongs to the sender and changes per
              job. "Issue without sending" stays separate — it is a different
              intent, not a different document. */}
          {['draft', 'proforma_sent'].includes(inv?.status) && (
            <Button variant="primary" onClick={() => setSendOpen(true)} loading={busy}>
              Send to client
            </Button>
          )}
          {inv?.status === 'draft' && (
            <Button variant="secondary" onClick={issue} disabled={busy}>
              Issue without sending
            </Button>
          )}
          {['issued', 'partly_paid'].includes(inv?.status) && (
            <>
              <Button variant="ghost" onClick={resend} disabled={busy}>Re-send</Button>
              <Button variant="primary" onClick={() => setPayOpen((o) => !o)}>Record receipt</Button>
            </>
          )}
          {inv && !['cancelled', 'paid'].includes(inv.status) && (
            <Button variant="secondary" onClick={cancel} disabled={busy}>Cancel invoice</Button>
          )}
        </>
      }
    >
      {loading || !inv ? <Skeleton.Text lines={8} /> : (
        <div className="fin-form">
          <div className="fin-inv-head">
            <span className={`fin-pill ${s.cls}`}>{s.label}</span>
            {inv.isOverdue && <span className="fin-pill fin-s-overdue">Overdue</span>}
            <span className="fin-muted">Dated {fmtDate(inv.invoiceDate)} · due {fmtDate(inv.dueDate)}</span>
          </div>

          {inv.status === 'proforma_sent' && (
            <p className="fin-note-warn">
              <b>{inv.proformaNumber}</b> is a proforma — no statutory number has been spent and
              nothing is owed yet. Confirming the client's payment issues the tax invoice
              automatically; issuing it by hand here does the same without waiting for payment.
            </p>
          )}
          {inv.proformaRef && (
            <p className="fin-sub" style={{ marginTop: 0 }}>Raised against proforma {inv.proformaRef}.</p>
          )}

          {sendInfo && (
            <div className="fin-sendinfo">
              <span className="fin-form-label">Client copy</span>
              {sendInfo.recipients.to.length ? (
                <p className="fin-sub" style={{ margin: '2px 0 6px' }}>
                  Goes to <b>{sendInfo.recipients.to.join(', ')}</b> — {sendInfo.recipients.label}.
                </p>
              ) : (
                <p className="fin-bad" style={{ margin: '2px 0 6px' }}>
                  No billing address on file — this client cannot be emailed. Set one on their
                  record.
                </p>
              )}
              {sendInfo.history.length > 0 && (
                <ul className="fin-sendlog">
                  {sendInfo.history.slice(0, 5).map((h) => (
                    <li key={h.id}>
                      <span className="fin-muted">{fmtDate(h.sentAt)}</span>
                      {' '}{h.kind.replace('_', ' ')}
                      {' → '}
                      {h.status === 'sent'
                        ? h.to.join(', ')
                        : <span className="fin-bad">{h.status}{h.error ? `: ${h.error}` : ''}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="fin-scroll">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Description</th><th>Type</th>
                  {/* Where the charge came from. The link back to the task was
                      always on the wire (lineItems.taskId) and was simply not
                      rendered, which left an invoice unable to explain itself. */}
                  <th>Source</th>
                  <th className="fin-num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.lineItems.map((l) => (
                  <tr key={l.id}>
                    <td>{l.description}</td>
                    <td className="fin-muted">{l.kind === 'ope' ? 'Reimbursement' : 'Fees'}</td>
                    <td className="fin-muted">
                      {l.taskId ? (
                        <Link className="fin-link" to={`/org-admin/tasks/${l.taskId}`}>Task</Link>
                      ) : l.sourceType === 'retainer' ? (
                        `Retainer${l.period ? ` · ${l.period}` : ''}`
                      ) : 'Typed by hand'}
                    </td>
                    <td className="fin-num">{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fin-preview">
            <div><span>Professional fees</span><b>{money(inv.taxableAmount)}</b></div>
            {inv.exemptAmount > 0 && <div><span>Reimbursements (no GST)</span><b>{money(inv.exemptAmount)}</b></div>}
            {inv.igst > 0 && <div><span>IGST @ {inv.gstRate}%</span><b>{money(inv.igst)}</b></div>}
            {inv.cgst > 0 && <div><span>CGST @ {inv.gstRate / 2}%</span><b>{money(inv.cgst)}</b></div>}
            {inv.sgst > 0 && <div><span>SGST @ {inv.gstRate / 2}%</span><b>{money(inv.sgst)}</b></div>}
            <div className="fin-preview-total"><span>Invoice total</span><b>{money(inv.total)}</b></div>
            {/* ⚠️ NO MINUS SIGN. This is what the client would withhold IF they
                are required to deduct — it is not taken off the receivable.
                "Still due" below stays the full balance until a payment records
                the TDS actually withheld, because that is the only point at
                which anyone knows whether it was. */}
            {inv.tdsAmount > 0 && (
              <div className="fin-preview-muted">
                <span>TDS if deducted @ {inv.tdsPercent}%</span><b>{money(inv.tdsAmount)}</b>
              </div>
            )}
            <div className="fin-preview-muted"><span>Received</span><b>{money(inv.amountPaid)}</b></div>
            {inv.tdsCredited > 0 && <div className="fin-preview-muted"><span>TDS credited</span><b>{money(inv.tdsCredited)}</b></div>}
            <div className="fin-preview-total"><span>Still due</span><b className={inv.amountDue > 0 ? 'fin-bad' : ''}>{money(inv.amountDue)}</b></div>
          </div>

          {payOpen && (
            <div className="fin-pay">
              <span className="fin-form-label">Record a receipt</span>
              <div className="fin-form-row">
                <Input
                  label="Amount received" type="number" min="0" step="0.01"
                  value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))}
                />
                <Input
                  label="TDS deducted" type="number" min="0" step="0.01"
                  value={pay.tdsAmount} onChange={(e) => setPay((p) => ({ ...p, tdsAmount: e.target.value }))}
                />
                <Input
                  label="Received on" type="date"
                  value={pay.receivedOn} onChange={(e) => setPay((p) => ({ ...p, receivedOn: e.target.value }))}
                />
              </div>
              <div className="fin-form-row">
                <Select label="Mode" value={pay.mode} onChange={(e) => setPay((p) => ({ ...p, mode: e.target.value }))}>
                  {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
                <Input
                  label="Reference (UTR / cheque no.)"
                  value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))}
                />
              </div>
              <p className="fin-sub">
                Enter the TDS the client actually withheld, separately from the cash — leave it empty
                if they paid in full. {inv.tdsAmount > 0 && (
                  <>If they deducted at {inv.tdsPercent}%, this ₹{inv.total.toLocaleString('en-IN')} invoice
                  settles with {money(inv.expectedReceipt)} in the bank plus {money(inv.tdsAmount)} of TDS.</>
                )}
              </p>
              <Button variant="primary" onClick={savePayment} loading={busy}>Save receipt</Button>
            </div>
          )}

          {data.payments.length > 0 && (
            <>
              <span className="fin-form-label">Receipts</span>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead><tr><th>Date</th><th>Receipt</th><th className="fin-num">Amount</th><th className="fin-num">TDS</th><th>Mode</th><th>Reference</th></tr></thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="fin-nowrap">{fmtDate(p.receivedOn)}</td>
                        <td className="fin-nowrap">
                          {p.receiptNumber
                            ? <button type="button" className="fin-linkbtn" onClick={() => viewReceipt(p.id)}>{p.receiptNumber}</button>
                            : <span className="fin-muted">—</span>}
                        </td>
                        <td className="fin-num">{money(p.amount)}</td>
                        <td className="fin-num fin-muted">{p.tdsAmount ? money(p.tdsAmount) : '—'}</td>
                        <td className="fin-muted">{p.mode.replace('_', ' ')}</td>
                        <td className="fin-muted">{p.reference || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>

    {sendOpen && inv && (
      <Modal
        isOpen
        onClose={() => setSendOpen(false)}
        title={`Send to ${inv.clientName}`}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => send(sendKind)} loading={busy}>
              {sendKind === 'proforma' ? 'Send proforma' : 'Send tax invoice'}
            </Button>
          </>
        }
      >
        <div className="fin-form">
          <span className="fin-form-label">Which document?</span>

          {/* Both are legitimate first contact. The copy states the consequence
              rather than the mechanism, because the number series is the part
              that is irreversible. */}
          <label className={`fin-choice${sendKind === 'proforma' ? ' is-picked' : ''}`}>
            <input
              type="radio" name="sendKind" value="proforma"
              checked={sendKind === 'proforma'}
              onChange={() => setSendKind('proforma')}
              disabled={!!inv.invoiceNumber}
            />
            <span>
              <b>Proforma invoice</b>
              {inv.proformaNumber && <span className="fin-muted"> · {inv.proformaNumber}</span>}
              <span className="fin-choice-note">
                "Here is what this will cost." Not a tax invoice, carries no input tax credit and
                nothing is owed yet. <b>No statutory number is spent</b> — the tax invoice is
                raised when payment is confirmed. Use this when the fee may still be queried, or
                when you bill in advance.
                {inv.invoiceNumber && <> Unavailable: a tax invoice has already been issued.</>}
              </span>
            </span>
          </label>

          <label className={`fin-choice${sendKind === 'tax_invoice' ? ' is-picked' : ''}`}>
            <input
              type="radio" name="sendKind" value="tax_invoice"
              checked={sendKind === 'tax_invoice'}
              onChange={() => setSendKind('tax_invoice')}
            />
            <span>
              <b>Tax invoice</b>
              {inv.invoiceNumber && <span className="fin-muted"> · {inv.invoiceNumber}</span>}
              <span className="fin-choice-note">
                The statutory document, straight away. <b>Consumes the next number in your invoice
                series</b> and becomes a receivable the moment it goes out — it can no longer be
                edited, only cancelled and re-raised. Use this for work already delivered on agreed
                terms.
                {inv.proformaNumber && !inv.invoiceNumber && <> It will cite proforma {inv.proformaNumber}.</>}
              </span>
            </span>
          </label>

          {sendInfo && (
            <p className={sendInfo.recipients.to.length ? 'fin-sub' : 'fin-bad'} style={{ margin: 0 }}>
              {sendInfo.recipients.to.length
                ? <>Goes to <b>{sendInfo.recipients.to.join(', ')}</b> — {sendInfo.recipients.label}.</>
                : <>No billing address on file — the document will be prepared and visible in the
                    portal, but nothing will be emailed.</>}
            </p>
          )}
        </div>
      </Modal>
    )}
    </>
  );
}

export default InvoiceDrawer;
